import Head from 'next/head'
import Link from 'next/link'

export default function FAQPage() {
  return (
    <>
    <Head>
      <title>FAQ — SkillBets</title>
      <meta name="description" content="Answers to common questions about SkillBets — sports prediction analytics, virtual betting, and how our AI works." />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="SkillBets" />
      <meta property="og:locale" content="en_ZA" />
      <meta property="og:title" content="FAQ — SkillBets" />
      <meta property="og:description" content="Answers to common questions about SkillBets — sports prediction analytics, virtual betting, and how our AI works." />
      <link rel="canonical" href="https://skillbets.co.za/faq/" />
      <meta property="og:url" content="https://skillbets.co.za/faq" />
      <meta property="og:image" content="https://skillbets.co.za/og-image.png" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@SkillBetsZA" />
      <meta name="twitter:title" content="FAQ — SkillBets" />
      <meta name="twitter:description" content="Answers to common questions about SkillBets — sports prediction analytics, virtual betting, and how our AI works." />
      <meta name="twitter:image" content="https://skillbets.co.za/og-image.png" />
      {/* FAQPage structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "What is SkillBets?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "SkillBets is a simulation and analytics platform for sports predictions. You practice by placing Virtual Currency on outcomes (no real money involved) and use our analytics—form, head-to-head, trend detection, and context—to improve your decision making before placing any real bets elsewhere. We do not accept deposits, pay out winnings, or act as a bookmaker."
                }
              },
              {
                "@type": "Question",
                "name": "Is this gambling? Do you pay out cash?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "No. SkillBets uses Virtual Currency only. There are no cash deposits or cash payouts and we are not a betting operator. Our goal is to help you practice scenarios and sharpen your analysis before you place any bets with a licensed sportsbook of your choice. Always gamble responsibly and within the law of your jurisdiction."
                }
              },
              {
                "@type": "Question",
                "name": "Who can use SkillBets? Is there an age restriction?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "SkillBets is intended for adults 18+."
                }
              },
              {
                "@type": "Question",
                "name": "What information do you collect during sign-up, and why?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "We ask for Username, Name, Surname, Telephone Number, Email, Password, Confirm Password, and Date of Birth. There's also an optional Promo Code field. Username helps you appear on leaderboards and identify your account. Name & Surname are used for account ownership and support. Telephone & Email are used for account security (verification, recovery) and important notices. Date of Birth confirms you are 18+. Password protects your account; we store it securely (hashed). Promo Code (optional) can unlock introductory benefits. We follow POPIA principles and only process data needed to run the service."
                }
              },
              {
                "@type": "Question",
                "name": "What analytics do I get on each plan?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Free: Core stats and recent results. Basic: Adds form charts, head-to-head, and simple trend flags. Premium: Adds advanced widgets (momentum, home/away splits, early fixture views), priority quotas, and Multi-Pick Pro."
                }
              },
              {
                "@type": "Question",
                "name": "Do you offer prizes, bonuses, or cash-outs?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "No. The current version of SkillBets focuses on practice and analysis only. There are no prizes, bonuses, or cash-outs."
                }
              },
              {
                "@type": "Question",
                "name": "How does SkillBets handle my personal data? (POPIA)",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "We follow POPIA principles: collect only what's necessary, store it securely, and use it for clear purposes (account, security, service improvement). You can request access, correction, or deletion of your data, subject to legal/operational requirements. See our Privacy Policy for full details."
                }
              },
              {
                "@type": "Question",
                "name": "I forgot my password. How do I reset it?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Use the Forgot password link on the sign-in screen. We'll email a secure reset link to the address on file. For account security, the link expires after a short period."
                }
              },
              {
                "@type": "Question",
                "name": "Can I delete my account?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes. Visit Account → Settings → Delete account or contact support@skillbets.co.za. Deletion is permanent and removes your personal data except where retention is required by law or to resolve active disputes."
                }
              },
              {
                "@type": "Question",
                "name": "Do you provide betting tips or guarantee profits?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "No. We provide tools and data to help you analyze matches and practice decision-making. Sports are unpredictable; there are no guaranteed outcomes. If you choose to bet with a licensed operator, do so responsibly."
                }
              },
              {
                "@type": "Question",
                "name": "How do I contact support?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Email us at support@skillbets.co.za. We aim to respond within one business day."
                }
              }
            ]
          })
        }}
      />
    </Head>
    <main className="min-h-screen bg-gray-900 text-white">
      {/* Top ribbon for compliance/context */}
      <div className="w-full bg-black/40 px-4 py-2 text-center text-[11px] text-white/70">
        Simulation & analytics for sports predictions • No cash wagering or payouts • SkillBets is not a bookmaker
      </div>

      {/* Header */}
      <header className="border-b border-gray-800 bg-gray-900">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-4">
          <Link href="/" className="flex items-center">
            <img src="/skillbets-logo.png" alt="SkillBets" className="h-14" />
          </Link>
          <Link
            href="/"
            className="rounded-xl bg-[#3EA8F5] px-3 py-2 text-xs font-bold text-gray-900 hover:brightness-110"
            data-testid="button-back-home"
          >
            Back to Home
          </Link>
        </div>
      </header>

      {/* Page header */}
      <section className="mx-auto max-w-5xl px-4 py-10">
        <h1 className="text-3xl sm:text-4xl font-extrabold">Frequently Asked Questions</h1>
        <p className="mt-3 max-w-3xl text-white/70">
          Everything you need to know about using SkillBets as a <strong>practice & analytics</strong> tool for sports predictions.
          If you still have questions, email <a className="underline text-[#3EA8F5]" href="mailto:support@skillbets.co.za">support@skillbets.co.za</a>.
        </p>
      </section>

      {/* FAQ list */}
      <section className="mx-auto max-w-5xl space-y-3 px-4 pb-16">
        {/* One QA per <details>. These are accessible, keyboard‑friendly, and require no JS. */}

        <details className="group rounded-2xl border border-white/10 bg-white/5 p-5 open:shadow-lg">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-4">
            <span className="text-base font-semibold">What is SkillBets?</span>
            <span className="shrink-0 text-white/60 group-open:rotate-45 transition-transform text-xl">＋</span>
          </summary>
          <div className="mt-3 text-white/80 text-sm leading-7">
            SkillBets is a <strong>simulation and analytics</strong> platform for sports predictions. You practice by placing
            <strong>Virtual Currency</strong> on outcomes (no real money involved) and use our analytics—form, head‑to‑head, trend
            detection, and context—to improve your decision making before placing any real bets elsewhere. We do <strong>not</strong>
            accept deposits, pay out winnings, or act as a bookmaker.
          </div>
        </details>

        <details className="group rounded-2xl border border-white/10 bg-white/5 p-5">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-4">
            <span className="text-base font-semibold">Is this gambling? Do you pay out cash?</span>
            <span className="shrink-0 text-white/60 group-open:rotate-45 transition-transform text-xl">＋</span>
          </summary>
          <div className="mt-3 text-white/80 text-sm leading-7">
            No. SkillBets uses <strong>Virtual Currency</strong> only. There are <strong>no cash deposits or cash payouts</strong> and we are <strong>not</strong> a
            betting operator. Our goal is to help you practice scenarios and sharpen your analysis before you place any
            bets with a licensed sportsbook of your choice. Always gamble responsibly and within the law of your
            jurisdiction.
          </div>
        </details>

        <details className="group rounded-2xl border border-white/10 bg-white/5 p-5">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-4">
            <span className="text-base font-semibold">Who can use SkillBets? Is there an age restriction?</span>
            <span className="shrink-0 text-white/60 group-open:rotate-45 transition-transform text-xl">＋</span>
          </summary>
          <div className="mt-3 text-white/80 text-sm leading-7">
            SkillBets is intended for <strong>adults 18+</strong>.
          </div>
        </details>

        <details className="group rounded-2xl border border-white/10 bg-white/5 p-5">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-4">
            <span className="text-base font-semibold">What information do you collect during sign‑up, and why?</span>
            <span className="shrink-0 text-white/60 group-open:rotate-45 transition-transform text-xl">＋</span>
          </summary>
          <div className="mt-3 text-white/80 text-sm leading-7 space-y-2">
            <p>We ask for <strong>Username, Name, Surname, Telephone Number, Email, Password, Confirm Password, and Date of Birth</strong>. There's also an optional <strong>Promo Code</strong> field.</p>
            <ul className="list-disc pl-5">
              <li><strong>Username</strong> helps you appear on leaderboards and identify your account.</li>
              <li><strong>Name & Surname</strong> are used for account ownership and support.</li>
              <li><strong>Telephone & Email</strong> are used for account security (verification, recovery) and important notices.</li>
              <li><strong>Date of Birth</strong> confirms you are 18+.</li>
              <li><strong>Password</strong> protects your account; we store it securely (hashed).</li>
              <li><strong>Promo Code</strong> (optional) can unlock introductory benefits.</li>
            </ul>
            <p className="text-white/60 text-xs">We follow <strong>POPIA</strong> principles and only process data needed to run the service. See our Privacy Policy for full details.</p>
          </div>
        </details>

        <details className="group rounded-2xl border border-white/10 bg-white/5 p-5">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-4">
            <span className="text-base font-semibold">What analytics do I get on each plan?</span>
            <span className="shrink-0 text-white/60 group-open:rotate-45 transition-transform text-xl">＋</span>
          </summary>
          <div className="mt-3 text-white/80 text-sm leading-7">
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Free</strong>: Core stats and recent results.</li>
              <li><strong>Basic</strong>: Adds form charts, head‑to‑head, and simple trend flags.</li>
              <li><strong>Premium</strong>: Adds advanced widgets (momentum, home/away splits, early fixture views), priority quotas, and Multi‑Pick Pro.</li>
            </ul>
          </div>
        </details>

        <details className="group rounded-2xl border border-white/10 bg-white/5 p-5">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-4">
            <span className="text-base font-semibold">Do you offer prizes, bonuses, or cash‑outs?</span>
            <span className="shrink-0 text-white/60 group-open:rotate-45 transition-transform text-xl">＋</span>
          </summary>
          <div className="mt-3 text-white/80 text-sm leading-7">
            No. The current version of SkillBets focuses on <strong>practice and analysis only</strong>. There are <strong>no prizes,
            bonuses, or cash‑outs</strong>.
          </div>
        </details>

        <details className="group rounded-2xl border border-white/10 bg-white/5 p-5">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-4">
            <span className="text-base font-semibold">How does SkillBets handle my personal data? (POPIA)</span>
            <span className="shrink-0 text-white/60 group-open:rotate-45 transition-transform text-xl">＋</span>
          </summary>
          <div className="mt-3 text-white/80 text-sm leading-7">
            We follow <strong>POPIA</strong> principles: collect only what's necessary, store it securely, and use it for clear
            purposes (account, security, service improvement). You can request access, correction, or deletion of your
            data, subject to legal/operational requirements. See our <strong>Privacy Policy</strong> for full details.
          </div>
        </details>

        <details className="group rounded-2xl border border-white/10 bg-white/5 p-5">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-4">
            <span className="text-base font-semibold">I forgot my password. How do I reset it?</span>
            <span className="shrink-0 text-white/60 group-open:rotate-45 transition-transform text-xl">＋</span>
          </summary>
          <div className="mt-3 text-white/80 text-sm leading-7">
            Use the <strong>Forgot password</strong> link on the sign‑in screen. We'll email a secure reset link to the address on
            file. For account security, the link expires after a short period.
          </div>
        </details>

        <details className="group rounded-2xl border border-white/10 bg-white/5 p-5">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-4">
            <span className="text-base font-semibold">Can I delete my account?</span>
            <span className="shrink-0 text-white/60 group-open:rotate-45 transition-transform text-xl">＋</span>
          </summary>
          <div className="mt-3 text-white/80 text-sm leading-7">
            Yes. Visit <strong>Account → Settings → Delete account</strong> or contact <strong>support@skillbets.co.za</strong>. Deletion is
            permanent and removes your personal data except where retention is required by law or to resolve active
            disputes.
          </div>
        </details>

        <details className="group rounded-2xl border border-white/10 bg-white/5 p-5">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-4">
            <span className="text-base font-semibold">Do you provide betting tips or guarantee profits?</span>
            <span className="shrink-0 text-white/60 group-open:rotate-45 transition-transform text-xl">＋</span>
          </summary>
          <div className="mt-3 text-white/80 text-sm leading-7">
            No. We provide <strong>tools and data</strong> to help you analyze matches and practice decision‑making. Sports are
            unpredictable; there are no guaranteed outcomes. If you choose to bet with a licensed operator, do so
            responsibly.
          </div>
        </details>

        <details className="group rounded-2xl border border-white/10 bg-white/5 p-5">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-4">
            <span className="text-base font-semibold">How do I contact support?</span>
            <span className="shrink-0 text-white/60 group-open:rotate-45 transition-transform text-xl">＋</span>
          </summary>
          <div className="mt-3 text-white/80 text-sm leading-7">
            Email us at <a className="underline text-[#3EA8F5]" href="mailto:support@skillbets.co.za">support@skillbets.co.za</a>.
            We aim to respond within one business day.
          </div>
        </details>
      </section>
    </main>
    </>
  );
}
